// Pick one of the user's secrets of the kinds a source or requirement accepts. With none, say
// so and offer to add one: in place when this browser is a linked device (it seals and signs
// the value), else through the Devices page (the draft survives the trip).
import { Plus, WarningCircle } from "@phosphor-icons/react";
import { Link } from "react-router";
import { Button } from "rizzui";

import { useT } from "../../../i18n";
import { SelectField } from "../../../ui/SideDrawer";
import type { Credential } from "../draft";

export interface CredentialOption {
  value: string;
  label: string;
}

export function CredentialSelect({
  label,
  matches,
  value,
  onChange,
  extra = [],
  onAdd,
  missing,
}: {
  label: string;
  matches: Credential[];
  value: string;
  onChange: (id: string) => void;
  /** More choices after the secrets, e.g. the platform key. */
  extra?: CredentialOption[];
  /** Opens the add-secret drawer; absent when this browser cannot add secrets. */
  onAdd?: () => void;
  /** What to say when there is nothing to choose. */
  missing: string;
}) {
  const t = useT().tasks.create.secrets;
  if (matches.length === 0 && extra.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-orange/50 bg-orange-lighter/40 p-3.5">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <p className="mb-0 mt-1 flex items-start gap-1.5 text-[13px] text-gray-700">
          <WarningCircle weight="fill" className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden /> {missing}
        </p>
        <div className="mt-3">
          {onAdd ? (
            <Button size="sm" className="gap-1.5" onClick={onAdd}>
              <Plus weight="bold" className="h-3.5 w-3.5" aria-hidden /> {t.add}
            </Button>
          ) : (
            <p className="m-0 text-[13px] text-gray-600">
              {t.needsDevice}{" "}
              <Link to="/devices" className="font-medium text-primary hover:underline">
                {t.linkDevice}
              </Link>
            </p>
          )}
        </div>
      </div>
    );
  }
  const options = [{ value: "", label: t.choose }, ...matches.map((c) => ({ value: c.id, label: c.name })), ...extra];
  return (
    <div>
      <SelectField label={label} value={value} options={options} onChange={onChange} />
      {onAdd ? (
        <button type="button" onClick={onAdd} className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline">
          <Plus weight="bold" className="h-3 w-3" aria-hidden /> {t.addAnother}
        </button>
      ) : null}
    </div>
  );
}
